import { useTranslation } from 'react-i18next';
import { WifiOff, CloudUpload } from 'lucide-react';

import { useOnlineStatus } from '@/shared/hooks/useOnlineStatus';
import { useOfflineQueue } from '@/shared/hooks/useOfflineQueue';

/**
 * Banner discreto en la parte superior cuando la app detecta que estamos
 * offline. Los datos que se ven son los últimos vistos (cache persistida).
 * Sprint I: los `createExpense` se encolan en IndexedDB y `useOfflineQueue`
 * los sincroniza al volver online.
 */
export function OfflineBanner() {
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const { pending } = useOfflineQueue();

  // Mientras hay pendientes en cola (aunque estemos online) mostramos un
  // banner azul de "sincronizando". Cuando el flush termine, `pending`
  // vuelve a 0 y el banner desaparece.
  if (online && pending === 0) return null;

  if (!online) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex items-center justify-center gap-2 bg-amber-500 px-3 py-1.5 text-[11px] font-medium text-white"
      >
        <WifiOff size={12} />
        <span>{t('common.offline_notice')}</span>
        {pending > 0 && (
          <span className="rounded bg-white/20 px-1.5 py-0.5">
            {t('offline.pending', { defaultValue: '{{count}} en cola', count: pending })}
          </span>
        )}
      </div>
    );
  }

  // Online + pending > 0 → sincronizando
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center justify-center gap-2 bg-blue-500 px-3 py-1.5 text-[11px] font-medium text-white"
    >
      <CloudUpload size={12} className="animate-pulse" />
      <span>{t('offline.syncing', { defaultValue: 'Sincronizando {{count}}…', count: pending })}</span>
    </div>
  );
}
